import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import { fg } from '@confluence/feature-gating';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { useContentReviewerNudge } from '../content-reviewer/useContentReviewerNudge.tsx';
import { useContentReviewerNudgeOnCriteriaBaseEligibility } from '../content-reviewer/useContentReviewerNudgeOnCriteriaBaseEligibility.ts';
import { useStatusUpdatesAutomationNudgeAction } from '../status-updates/useStatusUpdatesAutomationNudgeAction.tsx';

import { useConfluenceAutomationsBaseEligibility } from './useConfluenceAutomationsBaseEligibility.ts';
import { useStudioPermissions } from './useStudioPermissions.ts';

import type {
	useConfluenceAutomationNudgeActionsQuery$data,
	useConfluenceAutomationNudgeActionsQuery$variables,
} from './__generated__/useConfluenceAutomationNudgeActionsQuery.graphql.ts';

export interface AutomationNudgeActionEntry {
	actionId: string;
	action: ConversationAction;
}

/**
 * Runs the nudge-actions query and returns ONLY settled, content-matched data — never the raw
 * `useQueryInterop` result.
 *
 * `useQueryInterop` is stale-while-loading: during a content transition it keeps returning the
 * PREVIOUS page's `singleContent` (id !== the requested `contentId`, often with `loading: true`),
 * whose values are Relay fragment refs owned by the previous page's query. Reading any of them via
 * `useFragment` while that record is being torn down suspends synchronously → React #426 ("We hit a
 * snag"). By withholding at this boundary the discipline is enforced BY CONSTRUCTION: callers only
 * ever receive refs that belong to a settled query for the content they asked for, so no future
 * edit can accidentally hand a loading/stale ref to a sub-hook.
 */
const useConfluenceAutomationNudgeActionsQuery = ({
	contentId,
	includeStatusUpdatesAutomation,
	includeContentReviewerNudge,
	isDraft,
	skip,
}: {
	contentId: string;
	includeStatusUpdatesAutomation: boolean;
	includeContentReviewerNudge: boolean;
	isDraft: boolean;
	skip: boolean;
}): {
	singleContent: useConfluenceAutomationNudgeActionsQuery$data['singleContent'] | undefined;
	user: useConfluenceAutomationNudgeActionsQuery$data['user'] | undefined;
} => {
	const { data, loading } = useQueryInterop<
		useConfluenceAutomationNudgeActionsQuery$data,
		useConfluenceAutomationNudgeActionsQuery$variables
	>(
		graphql`
			query useConfluenceAutomationNudgeActionsQuery(
				$contentId: ID!
				$includeStatusUpdatesAutomation: Boolean!
				$includeContentReviewerNudge: Boolean!
				$isDraft: Boolean!
			) {
				user: confluence_atlassianUser(current: true) {
					__typename
					id
					...useStatusUpdatesAutomationNudgeAction_user
						@alias(as: "statusUpdatesAutomationUser")
						@include(if: $includeStatusUpdatesAutomation)
				}
				singleContent(id: $contentId) {
					__typename
					id
					...useContentReviewerNudgeOnCriteria_content
						@alias(as: "contentReviewerContent")
						@arguments(isDraft: $isDraft)
						@include(if: $includeContentReviewerNudge)
					...useStatusUpdatesAutomationNudgeAction_content
						@alias(as: "statusUpdatesAutomationContent")
						@include(if: $includeStatusUpdatesAutomation)
				}
			}
		`,
		{
			variables: {
				contentId,
				includeStatusUpdatesAutomation,
				includeContentReviewerNudge,
				isDraft,
			},
			skip,
		},
	);

	if (loading || !data) {
		return {
			singleContent: undefined,
			user: undefined,
		};
	}

	// Only expose `singleContent` once the query has SETTLED on the requested content: while a fetch
	// is in flight (`loading`) — or while stale-while-loading still returns the PREVIOUS content's
	// record (`id !== contentId`) — every fragment ref hung off it is withheld. `user` is not
	// content-scoped, so it only needs the loading guard.
	return {
		singleContent: data.singleContent?.id === contentId ? data.singleContent : undefined,
		user: data.user,
	};
};

export const useConfluenceAutomationNudgeActions = (
	contentId: string,
): AutomationNudgeActionEntry[] => {
	const hasBaseEligibility = useConfluenceAutomationsBaseEligibility();
	const pageMode = useGetPageMode();

	const hasUserSeenStatusUpdatesAutomationNudge = confluenceLocalStorageInstance.getItemAsBoolean(
		keys.AUTOMATION_DISCOVER_STATUS_UPDATES,
	);
	const includeStatusUpdatesAutomation =
		!hasUserSeenStatusUpdatesAutomationNudge && fg('cc_automations_discovery_nudges');

	const includeContentReviewerNudge = useContentReviewerNudgeOnCriteriaBaseEligibility();
	const isAnyNudgeEligible = includeStatusUpdatesAutomation || includeContentReviewerNudge;

	const { singleContent, user } = useConfluenceAutomationNudgeActionsQuery({
		contentId,
		includeStatusUpdatesAutomation,
		includeContentReviewerNudge,
		isDraft: pageMode === PageMode.EDIT,
		skip:
			!hasBaseEligibility ||
			!contentId ||
			!isAnyNudgeEligible ||
			!fg('automations-integration-to-rovo-button-nudges'),
	});

	const contentReviewerNudgeAction = useContentReviewerNudge({
		contentFragmentRef: singleContent?.contentReviewerContent,
		contentId,
	});

	const statusUpdatesAutomationNudgeAction = useStatusUpdatesAutomationNudgeAction({
		contentFragmentRef: singleContent?.statusUpdatesAutomationContent,
		userFragmentRef: user?.statusUpdatesAutomationUser,
	});

	const matchedActions = useMemo<AutomationNudgeActionEntry[]>(
		() =>
			[
				{ actionId: 'status-updates-automation', action: statusUpdatesAutomationNudgeAction },
				{ actionId: 'content-reviewer-nudge', action: contentReviewerNudgeAction },
			].filter((entry): entry is AutomationNudgeActionEntry => entry.action != null),
		[statusUpdatesAutomationNudgeAction, contentReviewerNudgeAction],
	);

	// Defer the Studio permission check until at least one nudge has matched, so
	// the permission query never runs for the common case where nothing matches —
	// while still only running it once for all nudges.
	const { canCreateStudioAutomation } = useStudioPermissions({ skip: matchedActions.length === 0 });

	return useMemo<AutomationNudgeActionEntry[]>(
		() => (canCreateStudioAutomation && hasBaseEligibility ? matchedActions : []),
		[matchedActions, canCreateStudioAutomation, hasBaseEligibility],
	);
};
